/**
 * PackageStep — weight, dimensions, contents, declared value, service class.
 *
 * Money values are STRINGS (per ADR-001). Dimensions are optional; if any
 * dimension field is filled, the validator requires all three.
 */
import type { JSX } from 'react';

import type { PackagePayload, ServiceClass } from '../../lib/api/types';

import { PackageSchema } from './validators/package.zod';

interface PackageStepProps {
  value: Partial<PackagePayload>;
  serviceClass?: ServiceClass;
  onChange: (next: Partial<PackagePayload>) => void;
  onServiceClassChange: (next: ServiceClass) => void;
  onContinue: () => void;
  onBack: () => void;
}

const SERVICE_CLASSES: { value: ServiceClass; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXPRESS', label: 'Express' },
  { value: 'EMS', label: 'EMS' },
  { value: 'REGISTERED_MAIL', label: 'Registered mail' },
];

export function PackageStep({
  value,
  serviceClass,
  onChange,
  onServiceClassChange,
  onContinue,
  onBack,
}: PackageStepProps): JSX.Element {
  const update = (patch: Partial<PackagePayload>): void => {
    onChange({ ...value, ...patch });
  };

  const updateDim = (axis: 'length' | 'width' | 'height', raw: string): void => {
    const current = value.dimensionsCm ?? { length: '', width: '', height: '' };
    onChange({ ...value, dimensionsCm: { ...current, [axis]: raw } });
  };

  const result = PackageSchema.safeParse(value);
  const isValid = result.success && serviceClass !== undefined;

  return (
    <section className="space-y-4" data-testid="package-step">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Package details</h2>
        <p className="text-sm text-slate-600">
          Capture weight, dimensions and content. Required fields marked with *.
        </p>
      </header>

      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="pkg-weight" className="text-xs font-medium text-slate-700">
              Weight (kg) *
            </label>
            <input
              id="pkg-weight"
              type="text"
              inputMode="decimal"
              value={value.weightKg ?? ''}
              onChange={(e) => update({ weightKg: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="pkg-declared" className="text-xs font-medium text-slate-700">
              Declared value (₦)
            </label>
            <input
              id="pkg-declared"
              type="text"
              inputMode="decimal"
              value={value.declaredValueNgn ?? ''}
              onChange={(e) => update({ declaredValueNgn: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <span className="text-xs font-medium text-slate-700">Dimensions (cm)</span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              <input
                placeholder="Length"
                aria-label="Length"
                value={value.dimensionsCm?.length ?? ''}
                onChange={(e) => updateDim('length', e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                placeholder="Width"
                aria-label="Width"
                value={value.dimensionsCm?.width ?? ''}
                onChange={(e) => updateDim('width', e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                placeholder="Height"
                aria-label="Height"
                value={value.dimensionsCm?.height ?? ''}
                onChange={(e) => updateDim('height', e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label htmlFor="pkg-content" className="text-xs font-medium text-slate-700">
              Content description *
            </label>
            <textarea
              id="pkg-content"
              rows={2}
              value={value.contentDescription ?? ''}
              onChange={(e) => update({ contentDescription: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="pkg-notes" className="text-xs font-medium text-slate-700">
              Notes
            </label>
            <textarea
              id="pkg-notes"
              rows={2}
              value={value.notes ?? ''}
              onChange={(e) => update({ notes: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <fieldset className="rounded border border-slate-200 bg-white p-4">
        <legend className="px-1 text-sm font-medium text-slate-700">Service class</legend>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {SERVICE_CLASSES.map((sc) => (
            <label
              key={sc.value}
              className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${
                serviceClass === sc.value
                  ? 'border-nipost-600 bg-nipost-50'
                  : 'border-slate-300 bg-white'
              }`}
            >
              <input
                type="radio"
                name="service-class"
                value={sc.value}
                checked={serviceClass === sc.value}
                onChange={() => onServiceClassChange(sc.value)}
              />
              <span>{sc.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!isValid}
          data-testid="package-continue"
          className="rounded bg-nipost-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
        >
          Continue
        </button>
      </div>
    </section>
  );
}
